import React from "react";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <>
      <div className="page-hero">
        <span className="page-hero-eyebrow">About Laura</span>
        <h1>From GM<br />to your<br /><em>silent partner.</em></h1>
        <p>Laura Hurle founded Peoplology after 15+ years inside some of Australia's most respected real estate agencies — from administration all the way to General Manager. That experience is not background. It is the product.</p>
      </div>

      <div className="stat-strip">
        <div className="stat-item"><span className="stat-num">15+</span><span className="stat-label">Years in real estate</span></div>
        <div className="stat-item"><span className="stat-num">6</span><span className="stat-label">Major agencies</span></div>
        <div className="stat-item"><span className="stat-num">150+</span><span className="stat-label">Placements made</span></div>
        <div className="stat-item"><span className="stat-num">100%</span><span className="stat-label">Real estate focused</span></div>
      </div>

      <section className="section section-black">
        <div style={{display:'block',maxWidth:'800px'}}>
          <span className="eyebrow">Laura's story</span>
          <h2 className="h-lg" style={{color:'var(--white)',marginBottom:'2rem'}}>The experience<br />is the product.</h2>
          <p className="body-lg" style={{marginBottom:'1.25rem'}}>Laura started in real estate administration. She finished as General Manager. In between, she held almost every role a real estate business has to offer — across some of Australia's most respected agencies, including Colliers International, Jellis Craig, Kay &amp; Burton, McGrath Estate Agents, Barry Plant and Hocking Stuart.</p>
          <p className="body-lg" style={{marginBottom:'1.25rem'}}>That journey was not a straight line. It was 15 years of learning what high-performance looks like from the inside — managing teams, making hiring decisions, navigating difficult exits, building cultures that hold together under pressure, and understanding what separates agencies that thrive from those that plateau.</p>
          <p className="body-lg" style={{marginBottom:'2rem'}}>She founded Peoplology because she kept watching the same story play out: brilliant boutique agencies, ambitious principals, genuine potential — held back not by the market, but by their people strategy. Wrong hires. Unclear culture. No retention plan. No one in their corner who truly understood the industry.</p>
          <div style={{borderLeft:'2px solid var(--gold)',paddingLeft:'2rem',marginBottom:'2.5rem'}}>
            <p style={{fontSize:'1.05rem',color:'rgba(255,255,255,0.7)',lineHeight:'1.75',fontStyle:'italic',fontWeight:300}}>“I built Peoplology because I kept seeing the same story play out — great agencies, struggling with people. I knew I could help. Not as a consultant who had read the textbooks, but as someone who had actually done it.”</p>
            <p style={{fontSize:'0.8rem',color:'var(--gold)',fontWeight:700,marginTop:'0.75rem'}}>— Laura Hurle, Founder</p>
          </div>
          <p className="body-lg">Peoplology exists to close the gap between ambition and execution — with clarity, honesty, and zero tolerance for mediocrity.</p>
        </div>
      </section>

      <section className="section section-black2" style={{paddingBottom:'60px'}}>
        <span className="eyebrow">Trusted by agencies I've called home</span>
        <h2 className="h-lg" style={{color:'var(--white)',marginBottom:'3rem'}}>The agencies that<br />shaped everything.</h2>
        <div style={{display:'flex',flexWrap:'wrap',gap:0,margin:'-4px'}}>
          {["Colliers International","Jellis Craig","Kay & Burton","McGrath Estate Agents","Barry Plant","Hocking Stuart"].map(a => (
            <span key={a} className="agency-badge">{a}</span>
          ))}
        </div>
        <p className="body-md" style={{marginTop:'2rem',maxWidth:'600px'}}>Laura's career spans some of Australia's most respected real estate brands — giving her an unmatched understanding of agency culture, operations, and people at every level of the business.</p>
      </section>

      <section className="section section-black">
        <div style={{marginBottom:'4rem'}}>
          <span className="eyebrow">Why Peoplology</span>
          <h2 className="h-lg" style={{color:'var(--white)'}}>We don't dabble.<br />We live it.</h2>
        </div>
        <div className="value-row">
          <div className="value-item">
            <span className="value-word">Bold.</span>
            <p>We say what needs to be said. If your culture is the problem, we'll tell you. If your salary isn't competitive, we'll say that too. Our advice is direct, our standards are high, and we don't soften the truth to make you feel comfortable.</p>
          </div>
          <div className="value-item">
            <span className="value-word">Deliberate.</span>
            <p>Every engagement is intentional. Every placement is personal. We work with a select group of agencies who are serious about their people and serious about growth. We are never a volume business.</p>
          </div>
          <div className="value-item">
            <span className="value-word">Matter‑of‑fact.</span>
            <p>No jargon, no corporate speak, no fluffy frameworks. We give you what you need — a clear picture of where you are, where you need to go, and exactly what it will take to get there.</p>
          </div>
        </div>
      </section>

      <section className="section" style={{background:'var(--gold)'}}>
        <div className="two-col" style={{maxWidth:'1100px'}}>
          <div>
            <h2 className="h-xl" style={{color:'var(--navy)',marginBottom:'1.25rem'}}>We're small.<br />That's the point.</h2>
            <p style={{fontSize:'1.1rem',color:'rgba(0,0,0,0.6)',lineHeight:'1.8',fontWeight:300,maxWidth:'460px'}}>When you work with Peoplology, you work with Laura — directly. No junior consultants, no handoffs, no one who hasn't walked the halls of a real estate office. That's not a limitation. It's the service.</p>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
            {[
              ["Direct access to Laura","Every conversation, every placement, every project — Laura is involved personally."],
              ["No junior handoffs","The person you brief is the person who does the work. No passing files between desks."],
              ["Personal service, every time","We deliberately stay small so we can stay personal. That's a deliberate choice, not a constraint."]
            ].map(([t,d])=>(
              <div key={t} style={{background:'rgba(0,0,50,0.10)',padding:'1.75rem',display:'flex',gap:'1rem',alignItems:'flex-start'}}>
                <div style={{width:'40px',height:'40px',background:'var(--navy)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:'var(--gold)',fontSize:'1rem'}}>✦</div>
                <div>
                  <div style={{fontSize:'0.9rem',fontWeight:800,color:'var(--navy)',marginBottom:'3px'}}>{t}</div>
                  <div style={{fontSize:'0.85rem',color:'rgba(0,0,0,0.55)',fontWeight:300}}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-black2">
        <div style={{marginBottom:'3.5rem'}}>
          <span className="eyebrow">What clients say</span>
          <h2 className="h-lg" style={{color:'var(--white)'}}>Real words.<br />Real people.</h2>
        </div>
        <div className="testi-grid" style={{gridTemplateColumns:'1fr',maxWidth:'800px'}}>
          <div className="testi">
            <span className="testi-mark">“</span>
            <p>Implementing a change management program in any organization comes with an enormous set of challenges, but Laura was never deterred. Her infectious personality, boundless enthusiasm, attention to detail and ability to rally the troops helped to make the impossible, possible. I only wish I could have taken Laura across the world to San Francisco with me, so we could continue working together — she is one of those rare people whose magic you wish you could bottle and give to others.</p>
            <div className="testi-name">Nadia K.</div>
            <div className="testi-role">CEO — Boutique Agency, Melbourne</div>
          </div>
          <div className="testi">
            <span className="testi-mark">“</span>
            <p>Laura possesses a boundless mindset of positivity and abundance and backs this up with a giant work ethic and ability to get things done. Laura is driven by a strong sense and vision of not only what is — but what can be.</p>
            <div className="testi-name">Name withheld by request</div>
            <div className="testi-role">Sales Manager — Melbourne</div>
          </div>
        </div>
      </section>

      <section className="section section-black" style={{textAlign:'center',borderTop:'1px solid var(--border)'}}>
        <h2 className="h-xl" style={{color:'var(--white)',marginBottom:'1.25rem'}}>Ready to work<br />with <em style={{fontStyle:'normal',color:'var(--gold)'}}>Laura?</em></h2>
        <p style={{fontSize:'1rem',color:'rgba(255,255,255,0.70)',fontWeight:300,maxWidth:'420px',margin:'0 auto 2.5rem',lineHeight:1.8}}>A 20-minute call is all it takes to know if we're the right fit for your agency.</p>
        <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
          <Link to="/contact" className="btn btn-gold">Book a discovery call</Link>
          <Link to="/services" className="btn btn-outline">View our services</Link>
        </div>
      </section>
    </>
  );
};

export default About;
